import { useState, useEffect } from 'react'
import { showSuccess, showError } from '../utils/toast'

// ── Mock data ───────────────────────────────────────────────────────────────
const recentActivity = [
  { id: 1, type: 'auto',   sf: 'Nexus Technology Group',     stripe: 'nexus technology',           reason: 'Contact email match',  time: '2h ago'     },
  { id: 2, type: 'agent',  sf: 'Atlas Consulting Group',      stripe: 'Atlas Consulting Group LLC', reason: 'Sarah Johnson',        time: '4h ago'     },
  { id: 3, type: 'auto',   sf: 'Vanguard Financial Services', stripe: 'Vanguard Financial Svc',     reason: 'Email domain match',   time: '6h ago'     },
  { id: 4, type: 'admin',  sf: 'Apex Industrial Supply',      stripe: 'Apex Industrial Supply LLC', reason: 'Admin approved',       time: 'Yesterday'  },
  { id: 5, type: 'auto',   sf: 'Coastal Media Group',         stripe: 'Coastal Media Grp',          reason: 'Email domain match',   time: 'Yesterday'  },
  { id: 6, type: 'reject', sf: 'BrightPath Consulting',       stripe: 'Brightpath Consult.',        reason: 'Admin rejected',       time: '2 days ago' },
  { id: 7, type: 'agent',  sf: 'Pinnacle Marketing Co',       stripe: 'Pinnacle Mktg',              reason: 'Mike Chen',            time: '2 days ago' },
  { id: 8, type: 'admin',  sf: 'Summit Healthcare Solutions', stripe: 'Summit Health Solutions',    reason: 'Admin approved',       time: '3 days ago' },
]

const agentStats = [
  { id: 'u1', initials: 'SJ', name: 'Sarah Johnson',   role: 'Account Executive', color: 'bg-blue-100 text-blue-700',    linked: 8, lastActive: '2h ago'     },
  { id: 'u3', initials: 'ER', name: 'Emily Rodriguez', role: 'SDR',               color: 'bg-green-100 text-green-700',  linked: 5, lastActive: 'Yesterday'  },
  { id: 'u2', initials: 'MC', name: 'Mike Chen',       role: 'Sales Rep',         color: 'bg-purple-100 text-purple-700',linked: 3, lastActive: 'Yesterday'  },
  { id: 'u4', initials: 'DP', name: 'David Park',      role: 'Account Executive', color: 'bg-amber-100 text-amber-700',  linked: 2, lastActive: '4 days ago' },
]

// ── Activity type badge ─────────────────────────────────────────────────────
function ActivityBadge({ type }) {
  const cfg = {
    auto:   { label: 'Auto',     cls: 'bg-stripe-purple-light text-stripe-purple border border-stripe-purple/20' },
    agent:  { label: 'Agent',    cls: 'bg-blue-50 text-blue-700 border border-blue-100' },
    admin:  { label: 'Approved', cls: 'bg-green-50 text-green-700 border border-green-100' },
    reject: { label: 'Rejected', cls: 'bg-red-50 text-red-600 border border-red-100' },
  }
  const { label, cls } = cfg[type]
  return <span className={`badge text-xs flex-shrink-0 ${cls}`}>{label}</span>
}

// ── Run Matching button ─────────────────────────────────────────────────────
function RunMatchingButton({ runState, lastRun, onClick }) {
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={onClick}
        disabled={runState !== 'idle'}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
          runState === 'idle'
            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
            : runState === 'running'
            ? 'bg-blue-50 border border-blue-200 text-blue-400 cursor-not-allowed'
            : 'bg-green-50 border border-green-200 text-green-700 cursor-default'
        }`}
      >
        {runState === 'idle' && (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
            Run Matching
          </>
        )}
        {runState === 'running' && (
          <>
            <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            Running…
          </>
        )}
        {runState === 'done' && (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Done
          </>
        )}
      </button>
      {lastRun && (
        <span className="text-xs text-gray-400">Last run: {lastRun}</span>
      )}
    </div>
  )
}

// ── Dashboard ───────────────────────────────────────────────────────────────
export default function Dashboard({ onNavigate }) {
  const totalSF  = 1247
  const matched  = 1089

  const [needsReview, setNeedsReview] = useState(4)
  const [runState, setRunState]       = useState('idle') // 'idle' | 'running' | 'done'
  const [newMatches, setNewMatches]   = useState(0)
  const [lastRun, setLastRun]         = useState(null)

  const unmatched = totalSF - matched - needsReview
  const matchPct  = Math.round((matched / totalSF) * 100)

  const handleRunMatching = async () => {
    setRunState('running')
    try {
      // Simulate API call; replace with real matching API when available
      await new Promise((resolve) => setTimeout(resolve, 2800))
      const found = 3
      setNewMatches(found)
      setNeedsReview((prev) => prev + found)
      setLastRun('just now')
      setRunState('done')
      showSuccess(`Matching complete — ${found} new matches found`)
    } catch (err) {
      console.error('Run matching failed:', err)
      setRunState('idle')
      showError(err?.message ?? 'Matching failed. Please try again.')
    }
  }

  // Auto-reset button to idle after showing 'done' for 5s
  useEffect(() => {
    if (runState !== 'done') return
    const t = setTimeout(() => setRunState('idle'), 5000)
    return () => clearTimeout(t)
  }, [runState])

  return (
    <div className="min-h-full bg-gray-50 px-4 py-10">
      {/* Header */}
      <div className="max-w-5xl mx-auto mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
              <span>BillJean</span><span>›</span><span>Dashboard</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">Acme Corp · Salesforce + Stripe</p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
              <span className="text-xs font-medium text-gray-600">Stripe connected</span>
              <span className="text-xs text-gray-300">·</span>
              <span className="text-xs text-gray-400">synced 5m ago</span>
            </div>
            <RunMatchingButton
              runState={runState}
              lastRun={lastRun}
              onClick={handleRunMatching}
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-5">

        {/* Matching complete banner */}
        {runState === 'done' && (
          <div className="flex items-center justify-between px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
              <span className="text-sm text-green-800">
                Matching complete —{' '}
                <span className="font-semibold">{newMatches} new matches</span> found and added to review
              </span>
            </div>
            <button
              onClick={() => onNavigate('matching')}
              className="text-xs font-semibold text-green-700 hover:text-green-900 transition-colors flex items-center gap-1"
            >
              Review now
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4">
          <div className="card p-4">
            <div className="text-2xl font-bold text-gray-900">{totalSF.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">Salesforce Accounts</div>
          </div>
          <div className="card p-4 border border-green-100 bg-green-50">
            <div className="text-2xl font-bold text-green-700">{matched.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">
              Matched <span className="text-green-600">({matchPct}%)</span>
            </div>
          </div>
          <div
            onClick={() => onNavigate('matching')}
            className="card p-4 border border-amber-100 bg-amber-50 cursor-pointer hover:border-amber-300 transition-colors"
          >
            <div className="text-2xl font-bold text-amber-600">{needsReview}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">
              Needs Review <span className="text-amber-500 font-semibold">→</span>
            </div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-gray-500">{unmatched}</div>
            <div className="text-xs text-gray-500 mt-0.5 font-medium">Unmatched</div>
          </div>
        </div>

        {/* Match coverage */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-semibold text-gray-800">Match Coverage</div>
              <div className="text-xs text-gray-400 mt-0.5">
                {matched.toLocaleString()} of {totalSF.toLocaleString()} Salesforce accounts matched to a Stripe customer
              </div>
            </div>
            <div className="text-2xl font-bold text-green-600">{matchPct}%</div>
          </div>
          {/* Segmented bar */}
          <div className="w-full h-2.5 rounded-full overflow-hidden flex gap-px bg-gray-100">
            <div className="h-full bg-green-500 rounded-l-full" style={{ width: `${(matched / totalSF) * 100}%` }} />
            <div className="h-full bg-amber-400" style={{ width: `${(needsReview / totalSF) * 100}%` }} />
            <div className="h-full bg-gray-200 rounded-r-full flex-1" />
          </div>
          <div className="flex items-center gap-5 mt-3">
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
              <span className="text-xs text-gray-400">Matched ({matched.toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
              <span className="text-xs text-gray-400">Needs Review ({needsReview})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-gray-200 flex-shrink-0" />
              <span className="text-xs text-gray-400">Unmatched ({unmatched})</span>
            </div>
          </div>
        </div>

        {/* Needs attention callout */}
        {needsReview > 0 && runState !== 'done' && (
          <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-center gap-2.5">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span className="text-sm text-amber-800">
                <span className="font-semibold">{needsReview} matches</span> are waiting for your review
              </span>
            </div>
            <button
              onClick={() => onNavigate('matching')}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors flex items-center gap-1"
            >
              Review now
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        )}

        {/* Activity + Agents */}
        <div className="grid grid-cols-5 gap-4">

          {/* Recent Activity */}
          <div className="col-span-3 card overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Recent Activity</span>
              <button
                onClick={() => onNavigate('matching')}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                View all →
              </button>
            </div>
            <div className="divide-y divide-gray-50">
              {recentActivity.map(item => (
                <div key={item.id} className="px-4 py-3 flex items-center gap-3">
                  <ActivityBadge type={item.type} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">{item.sf}</div>
                    <div className="text-xs text-gray-400 truncate">
                      → {item.stripe}
                      <span className="text-gray-300 mx-1">·</span>
                      {item.reason}
                    </div>
                  </div>
                  <div className="text-xs text-gray-300 flex-shrink-0">{item.time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Agent Overview */}
          <div className="col-span-2 card overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Active Agents</span>
              <button
                onClick={() => onNavigate('settings')}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Manage →
              </button>
            </div>
            <div className="divide-y divide-gray-50 flex-1">
              {agentStats.map(agent => (
                <div key={agent.id} className="px-4 py-3 flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${agent.color}`}>
                    {agent.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">{agent.name}</div>
                    <div className="text-xs text-gray-400 truncate">{agent.role}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs font-semibold text-gray-700">{agent.linked}</div>
                    <div className="text-xs text-gray-400">{agent.lastActive}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100">
              <span className="text-xs text-gray-400">4 of 10 agents active</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
